<script lang="ts" setup>
import { i18n } from '@/i18n';
import { useAuthenticationStore } from '@/stores/authentication/AuthenticationStore';
import { useModeStore } from '@/stores/mode/ModeStore';
import { useWhiteLabelStore } from '@/stores/white-label/WhiteLabelStore';
import { createActivationHead } from '@/utils/activation';
import { NeonSplashLoader } from '@arcual/lib-app-neon';
import { storeToRefs } from 'pinia';
import { useHead } from 'unhead';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { sharedRoutes } from './shared-routes';
import type { ActivationResponse, PublicActivation } from '@arcual/lib-app-platform';
import { useAccountStore } from '@/stores/account/AccountStore';

const ready = ref(false);
const head = useHead({});

const router = useRouter();
const route = useRoute();

const { activation } = storeToRefs(useWhiteLabelStore());

const initWhiteLabelling = async (activation: PublicActivation | ActivationResponse) => {
  if ('whiteLabelConfig' in activation) {
    const whiteLabelConfig = activation.whiteLabelConfig;

    // insert CSS
    const style = document.createElement('style');
    style.textContent = whiteLabelConfig.styles;
    document.head.append(style);
    // insert font rules into created stylesheet to avoid CORS issue
    const sheet = window.document.styleSheets[window.document.styleSheets.length - 1];
    whiteLabelConfig.fontRules.forEach((fontRule) => {
      sheet?.insertRule(fontRule, sheet.cssRules.length);
    });
    // merge i18n data
    Object.entries(whiteLabelConfig.copy).forEach(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ([key, value]) => (i18n.global.messages.value[key] = { ...i18n.global.messages.value[key], ...value }),
    );

    // update app metadata
    if (head) {
      head.patch(createActivationHead(activation, whiteLabelConfig));
    }
  }
  if ('linkInBioMetadata' in activation) {
    const linkInBioMetadata = activation.linkInBioMetadata;

    if (linkInBioMetadata?.style) {
      const style = document.createElement('style');
      style.textContent = linkInBioMetadata.style;
      document.head.append(style);
    }
  }
};

onMounted(async () => {
  if (useAuthenticationStore().isAuthenticated().value) {
    await useAccountStore().loadAccountProfile();
  }

  useModeStore().init();
});

onUnmounted(() => {
  if (head) {
    head.dispose();
  }
});

const unwatch = watch(
  () => [head, activation],
  async () => {
    if (head && typeof activation.value !== 'undefined') {
      // corner case, check if this is the /site-not/-found page & redirect to /
      if (route.path === sharedRoutes.SITE_NOT_FOUND) {
        await router.replace('/');
      }

      if (activation.value) {
        await initWhiteLabelling(activation.value);
      }

      unwatch();
      ready.value = true;
    }
  },
  {
    immediate: true,
    flush: 'sync',
    deep: true,
  },
);
</script>

<template>
  <router-view v-if="ready" v-slot="{ Component }">
    <transition mode="out-in" name="neon-fade-transition">
      <component :is="Component" />
    </transition>
  </router-view>
  <neon-splash-loader v-else />
</template>

<style lang="scss">
@use './global';
</style>
